// frontend/src/pages/master/VariantOptionMaster.jsx

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../utils/api';

const VariantOptionMaster = () => {
  const [tabs, setTabs] = useState([]); // 親：属性タイプの一覧
  const [options, setOptions] = useState([]); // 子：すべての選択肢一覧
  const [activeTabUuid, setActiveTabUuid] = useState(''); // 選択中の親UUID
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState(false); // 並び順保存中のローディング

  // タブ新規追加用のステート
  const [isAddingType, setIsAddingType] = useState(false);
  const [newType, setNewType] = useState({ option_type: '', label: '' });
  const inputRef = useRef(null);

  // タブの編集（変更）モード用のステート
  const [isEditingTypeMode, setIsEditingTypeMode] = useState(false);
  const [editTypeData, setEditTypeData] = useState({ option_type: '', label: '' });

  // 🌟 改善：2桁コードは裏側で自動採番するため、入力フォーム用のステートから完全に撤去
  const [formData, setFormData] = useState({
    name: '', color_code: '#000000', english_name: '', short_name: ''
  });

  const ICON_MAP = {
    color: { icon: 'fa-palette', colorClass: 'text-danger' },
    size: { icon: 'fa-ruler', colorClass: 'text-primary' },
    width: { icon: 'fa-shoe-prints', colorClass: 'text-warning' },
    length: { icon: 'fa-shoe-prints', colorClass: 'text-info' },
    type: { icon: 'fa-shapes', colorClass: 'text-success' },
    material: { icon: 'fa-layer-group', colorClass: 'text-secondary' },
    shape: { icon: 'fa-cube', colorClass: 'text-dark' },
    metal: { icon: 'fa-ring', colorClass: 'text-warning' },
    yukata_color: { icon: 'fa-palette', colorClass: 'text-danger' },
    obi_color: { icon: 'fa-palette', colorClass: 'text-warning' },
    yukata_pattern: { icon: 'fa-shapes', colorClass: 'text-success' }
  };

  useEffect(() => {
    loadMasterData();
  }, []);

  useEffect(() => {
    if (isAddingType && inputRef.current) inputRef.current.focus();
  }, [isAddingType]);

  const loadMasterData = async () => {
    setLoading(true);
    try {
      const [resTypes, resOptions] = await Promise.all([
        apiFetch('/types'),
        apiFetch('/')
      ]);

      if (resTypes.ok && resOptions.ok) {
        const typesData = await resTypes.json();
        const optionsData = await resOptions.json();

        setTabs(typesData);
        setOptions(optionsData);

        if (typesData.length > 0 && !activeTabUuid) {
          setActiveTabUuid(typesData[0].uuid);
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // 新しい属性タブを追加
  const handleAddNewTypeSubmit = async () => {
    const key = newType.option_type.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    const labelText = newType.label.trim() || key;
    if (!key) { setIsAddingType(false); return; }

    try {
      const res = await apiFetch('/types', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ option_type: key, label: labelText })
      });
      if (res.ok) {
        const createdType = await res.json();
        alert(`新しい属性「${labelText}」をデータベースに作成しました！`);
        setActiveTabUuid(createdType.uuid);
        loadMasterData();
      } else {
        alert((await res.json()).detail || '属性の追加に失敗しました。');
      }
    } catch (e) { alert('通信エラーが発生しました。'); }

    setIsAddingType(false);
    setNewType({ option_type: '', label: '' });
  };

  const handleAddNewTypeKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsAddingType(false);
      setNewType({ option_type: '', label: '' });
    }
  };

  // 属性（親タブ）自体の編集を開始
  const handleEditTypeStart = (currentType) => {
    setEditTypeData({ option_type: currentType.option_type, label: currentType.label });
    setIsEditingTypeMode(true);
  };

  // 属性（親タブ）自体の変更をサーバーへ送信
  const handleEditTypeSubmit = async () => {
    const key = editTypeData.option_type.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    const labelText = editTypeData.label.trim();
    if (!key || !labelText) { alert('IDと表示名は入力必須です。'); return; }

    try {
      const res = await apiFetch(`/variant-options/types/${activeTabUuid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ option_type: key, label: labelText })
      });
      if (res.ok) {
        alert('属性の定義を変更しました！');
        setIsEditingTypeMode(false);
        loadMasterData();
      } else {
        alert((await res.json()).detail || '属性の変更に失敗しました。');
      }
    } catch (e) { alert('通信エラーが発生しました。'); }
  };

  // 属性（親タブ）自体を丸ごと削除
  const handleDeleteType = async (currentType) => {
    const msg = `【超重要アラート】\n属性「${currentType.label} (${currentType.option_type})」をマスターから完全に削除します。\n\nこの属性に紐づいているすべての選択肢データも一緒に自動的に一括削除されますが、本当によろしいですか？`;
    if (!window.confirm(msg)) return;

    try {
      const res = await apiFetch(`/variant-options/types/${activeTabUuid}`, { method: 'DELETE' });
      if (res.ok) {
        alert(`属性「${currentType.label}」を一括削除しました。`);
        setActiveTabUuid('');
        setIsEditingTypeMode(false);
        loadMasterData();
      } else {
        alert((await res.json()).detail || '属性の削除に失敗しました。');
      }
    } catch (e) { alert('通信エラーが発生しました。'); }
  };

  // 選択肢（子）の登録送信
  const handleOptionSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await apiFetch('/variant-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, variant_type_uuid: activeTabUuid })
      });
      if (!res.ok) throw new Error((await res.json()).detail || '保存に失敗しました');

      alert('自動採番コードでマスタに新規登録しました！');
      setFormData({ name: '', color_code: '#000000', english_name: '', short_name: '' });
      loadMasterData();
    } catch (err) { alert(err.message); }
  };

  const handleOptionDelete = async (uuid) => {
    if (!window.confirm('この選択肢を削除しますか？')) return;
    try {
      const res = await apiFetch(`/variant-options/${uuid}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('削除に失敗しました');
      loadMasterData();
    } catch (err) { alert(err.message); }
  };

  // 🌟 修正：↑↓ボタンによる配列順序のローカル入れ替えロジック（JavaScript用の有効なコメント）
  const moveOrder = (index, direction) => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= filteredOptions.length) return;

    const updatedFiltered = [...filteredOptions];
    const temp = updatedFiltered[index];
    updatedFiltered[index] = updatedFiltered[targetIndex];
    updatedFiltered[targetIndex] = temp;

    const otherOptions = options.filter(o => o.variant_type_uuid !== activeTabUuid);
    setOptions([...otherOptions, ...updatedFiltered]);
  };

  // 🌟 修正：並び替えた現在の順序をバックエンドへ一括コミットして保存
  const handleSaveSortOrder = async () => {
    setSorting(true);
    const uuidsPayload = filteredOptions.map(o => o.uuid);
    try {
      const res = await apiFetch('/variant-options/sort', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uuids: uuidsPayload })
      });
      if (res.ok) {
        alert('✨ バリエーションの並び順をマスターに完全保存しました！');
        loadMasterData();
      } else {
        alert('並び順の保存に失敗しました。');
      }
    } catch (e) { alert('通信エラーが発生しました。'); }
    setSorting(false);
  };

  const activeTypeInfo = tabs.find(t => t.uuid === activeTabUuid) || { option_type: 'color', label: '属性' };

  // 🌟 修正：表示順（sort_order）に基づいて一覧を綺麗に整列
  const filteredOptions = options.filter(o => o.variant_type_uuid === activeTabUuid).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const isColorTab = activeTypeInfo.option_type === 'color' || activeTypeInfo.option_type.includes('color');

  return (
    <div className="pb-5">
      <div className="d-flex align-items-center mb-4">
        <div>
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item"><Link to="/">Home</Link></li>
            <li className="breadcrumb-item active">属性選択肢マスター</li>
          </ol>
          <h2 className="page-header mb-0 fw-bold">バリエーション属性 選択肢マスター管理</h2>
        </div>
      </div>

      {/* タブ一覧の並び */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-4 p-3 bg-light rounded shadow-sm border">
        {tabs.map(type => {
          const meta = ICON_MAP[type.option_type] || { icon: 'fa-tag', colorClass: 'text-indigo' };
          return (
            <button
              key={type.uuid}
              className={`btn fw-bold px-4 rounded-pill transition-all ${activeTabUuid === type.uuid ? 'btn-dark shadow' : 'btn-white border bg-white text-secondary'}`}
              onClick={() => { setActiveTabUuid(type.uuid); setIsAddingType(false); setIsEditingTypeMode(false); }}
            >
              <i className={`fa ${meta.icon} me-2 ${activeTabUuid === type.uuid ? 'text-white' : meta.colorClass}`}></i>
              {type.label}
            </button>
          );
        })}

        {isAddingType ? (
          <div className="d-flex align-items-center gap-1 p-1 bg-white border rounded-pill shadow-sm" style={{ paddingLeft: '12px' }}>
            <i className="fa fa-plus text-muted ms-1"></i>
            <input
              type="text" className="form-control form-control-sm border-0 shadow-none fw-bold bg-transparent" placeholder="ID (例: flavor)"
              style={{ width: '120px' }}
              value={newType.option_type} onChange={e => setNewType({ ...newType, option_type: e.target.value })}
              onKeyDown={handleAddNewTypeKeyDown}
            />
            <div className="vr opacity-25"></div>
            <input
              type="text" className="form-control form-control-sm border-0 shadow-none fw-bold bg-transparent" placeholder="表示名 (例: 味)"
              style={{ width: '120px' }}
              value={newType.label} onChange={e => setNewType({ ...newType, label: e.target.value })}
              onKeyDown={handleAddNewTypeKeyDown}
            />
            <button type="button" className="btn btn-sm btn-success rounded-circle shadow-sm ms-1" onClick={handleAddNewTypeSubmit}><i className="fa fa-check"></i></button>
            <button type="button" className="btn btn-sm btn-light rounded-circle text-muted mx-1" onClick={() => { setIsAddingType(false); setNewType({ option_type: '', label: '' }); }}><i className="fa fa-times"></i></button>
          </div>
        ) : (
          <button className="btn btn-outline-secondary fw-bold rounded-pill border-dashed px-3" onClick={() => setIsAddingType(true)}>
            <i className="fa fa-plus me-1"></i>新規属性
          </button>
        )}
      </div>

      <div className="row gx-4">
        {/* 左側：選択肢追加フォーム */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
            <div className="card-header bg-dark text-white fw-bold py-3">
              <i className="fa fa-plus-circle me-2"></i>{activeTypeInfo.label}に新しい選択肢を追加
            </div>
            <div className="card-body p-4">
              <div className="alert alert-info border-0 mb-3 small fw-bold text-indigo">
                <i className="fa fa-magic me-1"></i> 2桁コードはシステムが裏側で自動採番します。
              </div>
              <form onSubmit={handleOptionSubmit}>
                <div className="mb-3">
                  <label className="form-label small text-muted fw-bold">表示名 *</label>
                  <input type="text" className="form-control fw-bold form-control-lg" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="例: ホワイト、LLサイズ" required />
                </div>

                {isColorTab && (
                  <div className="mb-3 p-3 bg-light rounded border border-danger border-opacity-25">
                    <label className="form-label small fw-bold text-danger"><i className="fa fa-palette me-1"></i>色選択</label>
                    <div className="d-flex gap-2">
                      <input type="color" className="form-control form-control-color shadow-sm border-0" style={{ width: '60px', height: '38px', padding: '0' }} value={formData.color_code || '#000000'} onChange={e => setFormData({ ...formData, color_code: e.target.value })} />
                      <input type="text" className="form-control font-monospace" value={formData.color_code || ''} onChange={e => setFormData({ ...formData, color_code: e.target.value })} placeholder="#000000" />
                    </div>
                  </div>
                )}

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small text-muted fw-bold">英語名</label>
                    <input type="text" className="form-control" value={formData.english_name} onChange={e => setFormData({ ...formData, english_name: e.target.value })} placeholder="例: White" />
                  </div>
                  <div className="col-6">
                    <label className="form-label small text-muted fw-bold">省略名</label>
                    <input type="text" className="form-control font-monospace" value={formData.short_name} onChange={e => setFormData({ ...formData, short_name: e.target.value })} placeholder="例: WHT" />
                  </div>
                </div>

                <button type="submit" className="btn btn-success w-100 fw-bold mt-2 shadow-sm py-2">
                  <i className="fa fa-save me-2"></i>この内容でマスタ登録
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* 右側：登録済み一覧テーブル（ソート矢印付き） */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom pt-4 pb-3 d-flex align-items-center justify-content-between">
              {isEditingTypeMode ? (
                <div className="d-flex align-items-center gap-2 w-100 bg-warning bg-opacity-10 p-2 rounded border border-warning border-opacity-25">
                  <span className="small fw-bold text-warning font-monospace">属性編集:</span>
                  <input type="text" className="form-control form-control-sm fw-bold font-monospace" style={{ width: '130px' }} value={editTypeData.option_type} onChange={e => setEditTypeData({ ...editTypeData, option_type: e.target.value })} />
                  <input type="text" className="form-control form-control-sm fw-bold" style={{ width: '130px' }} value={editTypeData.label} onChange={e => setEditTypeData({ ...editTypeData, label: e.target.value })} />
                  <button type="button" className="btn btn-sm btn-success fw-bold px-3 shadow-sm" onClick={handleEditTypeSubmit}><i className="fa fa-save me-1"></i>保存</button>
                  <button type="button" className="btn btn-sm btn-light border px-2 text-muted" onClick={() => setIsEditingTypeMode(false)}><i className="fa fa-times"></i></button>
                </div>
              ) : (
                <>
                  <h5 className="mb-0 fw-bold">
                    <i className={`fa ${ICON_MAP[activeTypeInfo.option_type]?.icon || 'fa-list'} text-primary me-2`}></i>
                    登録済みの {activeTypeInfo.label} 一覧
                  </h5>

                  <div className="d-flex gap-2">
                    {/* 🌟 並び順を確定させる一括コミットボタン */}
                    {filteredOptions.length > 1 && (
                      <button type="button" className="btn btn-sm btn-indigo fw-bold px-3 rounded shadow-sm text-white" onClick={handleSaveSortOrder} disabled={sorting}>
                        {sorting ? <i className="fa fa-spinner fa-spin me-1"></i> : <i className="fa fa-sort-amount-down me-1"></i>}
                        並び順をマスターに確定保存
                      </button>
                    )}

                    {activeTypeInfo.option_type !== 'color' && activeTypeInfo.option_type !== 'size' && (
                      <>
                        <button type="button" className="btn btn-xs btn-outline-warning fw-bold px-2 rounded" onClick={() => handleEditTypeStart(activeTypeInfo)}>
                          <i className="fa fa-edit"></i>
                        </button>
                        <button type="button" className="btn btn-xs btn-outline-danger fw-bold px-2 rounded" onClick={() => handleDeleteType(activeTypeInfo)}>
                          <i className="fa fa-trash"></i>
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light small text-muted">
                    <tr>
                      <th className="text-center" style={{ width: '100px' }}>並び順順序</th>
                      <th className="text-center" style={{ width: '80px' }}>コード</th>
                      {isColorTab && <th style={{ width: '70px' }}>色</th>}
                      <th>表示名</th>
                      <th>英語名 / 省略名</th>
                      <th className="text-center" style={{ width: '80px' }}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? <tr><td colSpan="6" className="text-center py-5"><i className="fa fa-spinner fa-spin me-2"></i>読み込み中...</td></tr> : filteredOptions.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-5 text-muted">
                          <i className="fa fa-folder-open fa-2x mb-3 opacity-25 d-block"></i>
                          登録データがありません。
                        </td>
                      </tr>
                    ) : (
                      filteredOptions.map((o, idx) => (
                        <tr key={o.uuid}>
                          {/* 🌟 ↑↓矢印ボタンで直感的にローカル配列をシャッフル */}
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-1">
                              <button type="button" className="btn btn-xs btn-white border px-2 shadow-sm" onClick={() => moveOrder(idx, 'up')} disabled={idx === 0} title="上へ移動">
                                <i className="fa fa-chevron-up text-muted" style={{ fontSize: '11px' }}></i>
                              </button>
                              <button type="button" className="btn btn-xs btn-white border px-2 shadow-sm" onClick={() => moveOrder(idx, 'down')} disabled={idx === filteredOptions.length - 1} title="下へ移動">
                                <i className="fa fa-chevron-down text-muted" style={{ fontSize: '11px' }}></i>
                              </button>
                            </div>
                          </td>
                          <td className="text-center font-monospace fw-bold text-primary">{o.code}</td>
                          {isColorTab && (
                            <td>
                              <div className="border rounded shadow-sm" style={{ width: '28px', height: '28px', backgroundColor: o.color_code || 'transparent' }}></div>
                            </td>
                          )}
                          <td className="fw-bold fs-6">{o.name}</td>
                          <td>
                            {o.english_name ? <span className="text-dark me-2">{o.english_name}</span> : <span className="text-muted opacity-5 me-2">--</span>}
                            {o.short_name ? <span className="badge bg-light text-muted border font-monospace px-2">{o.short_name}</span> : ''}
                          </td>
                          <td className="text-center">
                            <button type="button" className="btn btn-sm btn-outline-danger shadow-sm px-2" onClick={() => handleOptionDelete(o.uuid)}>
                              <i className="fa fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariantOptionMaster;